import React, { useCallback, useEffect, useRef, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import i18n from 'i18n-js';
import { Box, Grid, Tooltip } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import * as Yup from 'yup';
import moment from 'moment';
import { BaseModal } from 'components/UI';
import { Formik, Form, Field } from 'formik';
import { useStyles } from './ReservationModal';
import WarningIcon from '@material-ui/icons/Warning';
import { ReservationsActions } from 'state/modules/reservations';
import { buildUrl } from 'utils';
import {
  DateTimePickerField,
  CheckboxField,
  RadioButtonsGroup,
  RadioButton,
} from 'components/FormFields';
import {
  VehiclesSelect,
  UsersSelect,
  VehiclesGroupsSelect,
  UserGroupsSelect,
} from 'components/AsyncFields';

import { UserContext } from 'context';

const UpdateReservationModal = ({ id, user, open, handleClick }) => {
  const [selectedUserGroup, setSelectedUserGroup] = useState();
  const [selectedVehicleType, setSelectedVehicleType] = useState('ACTIVE');
  const classes = useStyles();
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    fetchedReservationSuccess,
    createReservationError,
    reservationLoading,
    reservation,
    errors,
  } = useSelector((state) => state.reservations);
  const { fetchingVehicles } = useSelector((state) => state.vehicles);
  const { orgType } = useContext(UserContext);

  // Validation schema of update user form
  const UpdateReservationSchema = Yup.object().shape({
    user_id: Yup.number().required(i18n.t('common.required_field')),
    vehicle_id: Yup.number().required(i18n.t('common.required_field')),
    vehicle_group_id: Yup.number().required(i18n.t('common.required_field')),
    end_reserved: Yup.string().nullable().required(i18n.t('common.required_field')),
    start_reserved: Yup.string().nullable().required(i18n.t('common.required_field')),
  });

  // Initials values of the form
  const initialValues = {
    timezone: '',
    user_id: user?.id || '',
    vehicle_id: reservation?.vehicle?.id || '',
    vehicle_group_id: reservation?.vehicle_group?.id || '',
    end_reserved: reservation?.end_reserved || moment().add(3, 'hour'),
    start_reserved: reservation?.start_reserved || moment().add(1, 'hour'),
    options: { bypass_rules: false, skip_buffer: false },
  };

  const onCancel = useCallback(() => {
    handleClick();
    dispatch(ReservationsActions.resetUpdateReservation());
  }, [dispatch, handleClick]);

  useEffect(() => {
    if (fetchedReservationSuccess) {
      onCancel();
    }
  }, [fetchedReservationSuccess, onCancel]);

  useEffect(() => {
    dispatch(ReservationsActions.resetUpdateReservation());
    if (id) {
      dispatch(ReservationsActions.fetchReservationById(id));
    }
  }, [dispatch, id]);

  /**
   * Handle form submit
   * @param {object} values Object with the value of fields of the form
   */
  const onSubmit = async (values) => {
    if (id) {
      const payload = { ...values };
      delete payload.timezone;
      delete payload.vehicle_group_id;
      return dispatch(ReservationsActions.updateReservation(id, payload));
    }
    const result = await dispatch(ReservationsActions.createReservation(values));
    if (result?.payload?.id) {
      history.push(buildUrl(`/reservations/${result.payload.id}`));
    }
    return result;
  };

  const handleSubmit = () => {
    formRef.current.submitForm();
  };

  // eslint-disable-next-line react/prop-types
  const TimezoneComponent = ({ tz = moment.tz.guess() }) => {
    const date = moment().tz(tz);
    return (
      <Box>
        {i18n.t('reservations.different_timezone', {
          time_zone: date.format('UTC (Z)'),
          time: date.format('hh:mm A'),
        })}
      </Box>
    );
  };

  return (
    <BaseModal
      size="md"
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={handleSubmit}
      onCancel={onCancel}
      closeButtonType="icon"
      enableCloseButton={false}
      loading={reservationLoading}
      data-cy="reservation-modal"
      title={
        id
          ? i18n.t('reservations.update_reservation')
          : i18n.t('reservations.create_new_reservation')
      }
      enterButtonLabel={
        id ? i18n.t('reservations.update_reservation') : i18n.t('reservations.create_reservation')
      }
      privileges={{ action: id ? 'update' : 'create', subject: 'reservation' }}
    >
      <Grid container direction="column" alignItems="flex-start" data-cy="create-reservation-modal">
        <Box width="100%">
          <Formik
            innerRef={formRef}
            onSubmit={onSubmit}
            initialValues={initialValues}
            validationSchema={UpdateReservationSchema}
          >
            {({ handleSubmit, values, errors: err, touched, setFieldValue }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Grid item xs={12}>
                  <Field
                    form={{
                      setFieldValue: (field, value) => setSelectedUserGroup(value || undefined),
                    }}
                    field={{
                      name: 'user_group_id',
                      value: selectedUserGroup || undefined,
                    }}
                    onChange={() => {
                      setFieldValue('user_id', '');
                      setFieldValue('vehicle_group_id', '');
                      setFieldValue('vehicle_id', '');
                    }}
                    label={i18n.t('common.user_groups')}
                    component={UserGroupsSelect}
                  />
                </Grid>
                <Grid container spacing={2}>
                  {!user && (
                    <Grid item xs={12}>
                      <Box>
                        <Field
                          type="users"
                          margin="none"
                          name="user_id"
                          id="usersSelect"
                          disabled={!selectedUserGroup}
                          customParams={{
                            user_group_id: selectedUserGroup,
                          }}
                          inputProps={{
                            'data-cy': 'users-selector',
                          }}
                          onChange={() => {
                            setFieldValue('vehicle_group_id', '');
                            setFieldValue('vehicle_id', '');
                          }}
                          component={UsersSelect}
                          label={i18n.t('common.user')}
                        />
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Box>
                      <Field
                        margin="none"
                        id="vehicleGroupSelect"
                        name="vehicle_group_id"
                        timezoneField="timezone"
                        user_id={values?.user_id}
                        disabled={!values?.user_id}
                        component={VehiclesGroupsSelect}
                        inputProps={{
                          'data-cy': 'vehicle-group-select',
                        }}
                        label={i18n.t(
                          ['msServiceOrg', 'plainOrg'].includes(orgType)
                            ? 'vehicle.vehicle_group'
                            : 'vehicle.service_area'
                        )}
                        onChange={(val) => {
                          setFieldValue('vehicle_id', '');
                          setFieldValue(
                            'end_reserved',
                            moment()
                              .tz(val?.time_zone || moment.tz.guess())
                              .add(3, 'hour')
                              .format()
                          );
                          setFieldValue(
                            'start_reserved',
                            moment()
                              .tz(val?.time_zone || moment.tz.guess())
                              .add(1, 'hour')
                              .format()
                          );
                        }}
                        customParams={{
                          group_type: orgType === 'msOwnerOrg' ? 'OWNER' : 'CUSTOMER',
                          include_children: true,
                        }}
                      />
                    </Box>
                  </Grid>
                  {values?.vehicle_id && values?.timezone && (
                    <Grid item xs={12}>
                      <Box className={classes.timezoneBox}>
                        <WarningIcon className={classes.warningIcon} />
                        <TimezoneComponent tz={values?.timezone} />
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Box>
                      <Field
                        editable={true}
                        InputProps={{ 'data-cy': 'start-reserved-picker' }}
                        isClearable={false}
                        minDate={new Date()}
                        name="start_reserved"
                        timezone={values.timezone}
                        component={DateTimePickerField}
                        asyncErrors={errors?.start_time}
                        label={i18n.t('reservations.start_date')}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box>
                      <Field
                        editable={true}
                        InputProps={{ 'data-cy': 'end-reserved-picker' }}
                        name="end_reserved"
                        isClearable={false}
                        timezone={values.timezone}
                        asyncErrors={errors?.end_time}
                        component={DateTimePickerField}
                        label={i18n.t('reservations.end_date')}
                        minDate={moment(values.start_reserved).toDate() || new Date()}
                      />
                    </Box>
                  </Grid>
                  {orgType === 'msServiceOrg' && (
                    <Grid item xs={12}>
                      <Box>
                        <RadioButtonsGroup
                          errors={err}
                          touched={touched}
                          name="vehicles_type"
                          value={selectedVehicleType}
                          disabled={!values.vehicle_group_id}
                          setFieldValue={(field, value) => {
                            setSelectedVehicleType(value);
                          }}
                          nonBool
                          row
                        >
                          <Field
                            value={'ACTIVE'}
                            component={RadioButton}
                            label={i18n.t('status.ACTIVE')}
                          />
                          <Field
                            value={'INSHOP'}
                            component={RadioButton}
                            label={i18n.t('status.INSHOP')}
                          />
                        </RadioButtonsGroup>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Box>
                      <Field
                        labelPlacement="end"
                        component={CheckboxField}
                        field={{
                          name: 'options.skip_buffer',
                          value: values?.options?.skip_buffer,
                        }}
                        label={
                          <div className={classes.checkboxLabel}>
                            <Box marginRight="0.5rem">{i18n.t('reservations.skip_buffer')}</Box>
                            <Tooltip
                              title={i18n.t('reservations.skip_buffer_tooltip')}
                              placement="right"
                            >
                              <InfoIcon fontSize="small" />
                            </Tooltip>
                          </div>
                        }
                        disabled={!values.vehicle_group_id}
                        className={classes.checkField}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box>
                      <Field
                        name="vehicle_id"
                        id="reservation_vehicle_select"
                        component={VehiclesSelect}
                        label={i18n.t('common.vehicle')}
                        disabled={!values?.vehicle_group_id || fetchingVehicles === 'LOADING'}
                        vehicle_group_id={values?.vehicle_group_id}
                        inputProps={{ 'data-cy': 'reservation-vehicle-select' }}
                        customParams={{
                          exclude_unshared: true,
                          skip_buffer: values?.options?.skip_buffer,
                          start_time:
                            typeof values?.start_reserved === 'string'
                              ? values?.start_reserved
                              : null,
                          end_time:
                            typeof values?.end_reserved === 'string' ? values?.end_reserved : null,
                          ...(orgType === 'msServiceOrg'
                            ? {
                                status_filter: 'only',
                                statuses: selectedVehicleType,
                              }
                            : {}),
                        }}
                        customNoOptionText={i18n.t('reservations.no_vehicles_by_date')}
                      />
                    </Box>
                  </Grid>
                  {createReservationError && (
                    <Grid item xs={12}>
                      <Box>
                        <Field
                          name="bypass"
                          labelPlacement="end"
                          field={{
                            name: 'options.bypass_rules',
                            value: values?.options?.bypass_rules,
                          }}
                          component={CheckboxField}
                          className={classes.checkField}
                          label={
                            orgType === 'msOwnerOrg'
                              ? i18n.t('reservations.bypass_rules')
                              : i18n.t('reservations.bypass')
                          }
                        />
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Form>
            )}
          </Formik>
        </Box>
      </Grid>
    </BaseModal>
  );
};

UpdateReservationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  user: PropTypes.object,
};

UpdateReservationModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default UpdateReservationModal;
